import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvoiceGenerationDashboard from './components/InvoiceGenerationDashboard';
import InvoiceList from './components/InvoiceList';
import InvoiceAnalytics from './components/InvoiceAnalytics';
import GenerateInvoiceModal from './components/GenerateInvoiceModal';
import { invoiceService } from '../../services/invoiceService';

const AutomatedInvoiceManagementSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [filters, setFilters] = useState({
    paymentStatus: '',
    startDate: '',
    endDate: ''
  });
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingInvoices: 0,
    completedInvoices: 0,
    failedInvoices: 0,
    totalRevenue: 0,
    pendingAmount: 0
  });

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadInvoices();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin, filters]);

  const loadInvoices = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await invoiceService?.getBillingRecords(currentTenant?.id, filters);
      
      if (result?.success) {
        const invoiceData = result?.data || [];
        setInvoices(invoiceData);

        // Calculate stats
        const totalInvoices = invoiceData?.length;
        const pendingInvoices = invoiceData?.filter(inv => inv?.payment_status === 'pending')?.length;
        const completedInvoices = invoiceData?.filter(inv => inv?.payment_status === 'completed')?.length;
        const failedInvoices = invoiceData?.filter(inv => inv?.payment_status === 'failed')?.length;
        const totalRevenue = invoiceData
          ?.filter(inv => inv?.payment_status === 'completed')
          ?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0);
        const pendingAmount = invoiceData
          ?.filter(inv => inv?.payment_status === 'pending')
          ?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0);

        setStats({
          totalInvoices,
          pendingInvoices,
          completedInvoices,
          failedInvoices,
          totalRevenue,
          pendingAmount
        });
      } else {
        throw new Error(result?.error);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load invoices');
      console.error('Invoice load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const result = await invoiceService?.downloadInvoice(invoiceId, currentTenant?.id);
      if (!result?.success) {
        alert(result?.error || 'Failed to download invoice');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download invoice');
    }
  };

  const handleSendInvoiceEmail = async (invoiceId) => {
    try {
      const result = await invoiceService?.sendInvoiceEmail(invoiceId, currentTenant?.id);
      if (result?.success) {
        alert('Invoice email sent successfully!');
      } else {
        alert(result?.error || 'Failed to send invoice email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      alert('Failed to send invoice email');
    }
  };

  const handleGenerateInvoice = async (subscriptionId) => {
    try {
      const result = await invoiceService?.generateInvoiceForSubscription(
        subscriptionId,
        currentTenant?.id
      );
      
      if (result?.success) {
        alert('Invoice generated successfully!');
        setShowGenerateModal(false);
        await loadInvoices();
      } else {
        alert(result?.error || 'Failed to generate invoice');
      }
    } catch (error) {
      console.error('Generate invoice error:', error);
      alert('Failed to generate invoice');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invoice data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-6 pb-24 md:pb-6">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Automated Invoice Management
            </h1>
            <p className="text-muted-foreground">
              Generate, manage, and track membership invoices
            </p>
          </div>
          <Button
            onClick={() => setShowGenerateModal(true)}
            iconName="Plus"
            iconSize={20}
          >
            Generate Invoice
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-error">{error}</p>
          </div>
        )}

        {/* Dashboard Stats */}
        <InvoiceGenerationDashboard stats={stats} />

        {/* Analytics */}
        <InvoiceAnalytics invoices={invoices} />

        {/* Invoice List */}
        <InvoiceList
          invoices={invoices}
          filters={filters}
          onFilterChange={setFilters}
          onDownloadInvoice={handleDownloadInvoice}
          onSendEmail={handleSendInvoiceEmail}
          onRefresh={loadInvoices}
        />
      </main>

      {/* Generate Invoice Modal */}
      {showGenerateModal && (
        <GenerateInvoiceModal
          tenantId={currentTenant?.id}
          onClose={() => setShowGenerateModal(false)}
          onGenerate={handleGenerateInvoice}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default AutomatedInvoiceManagementSystem;